import { DepthLevel, LengthLevel } from '../types';
import { Tier } from '../api/paymentsApi';

// ── Monetization model (confirmed) ─────────────────────────────────────────────
// FREE (ad-supported): can generate a BASIC podcast — short length, default 2
// voices, no tone/voice customization. Everything else (longer podcasts, more
// voices, tone, custom voice, audiobooks, long direct-read) needs a subscription
// OR a one-off pay-per-item unlock.

export const TIER_RANK: Record<Tier, number> = { free: 0, basic: 1, pro: 2 };

export const FREE_PODCAST_DEPTHS: DepthLevel[] = ['brief', 'moderate'];
export const DEFAULT_PODCAST_SPEAKERS = 2;
export const FREE_TTS_CHAR_CAP = 1000;
export const MAX_TTS_CHARS = 50000;

export type GenFormat = 'podcast' | 'audiobook' | 'tts';

export interface GenOptions {
  format: GenFormat;
  depthLevel?: DepthLevel;
  lengthLevel?: LengthLevel;
  numSpeakers?: number;
  tone?: string;
  customVoice?: boolean;
  ttsCharCount?: number;
}

export interface GateLock {
  option: string;
  label: string;
  requiredTier: Tier;
}

export interface GateResult {
  allowed: boolean;
  requiredTier: Tier | null; // highest tier needed to unlock via subscription
  payPerItemEligible: boolean; // can be unlocked as a single one-off purchase
  locks: GateLock[];
}

function higher(a: Tier | null, b: Tier): Tier {
  if (!a) return b;
  return TIER_RANK[a] >= TIER_RANK[b] ? a : b;
}

// Determines whether the given generation is allowed for the user's tier, and if
// not, what unlocks it. A single generation can always be unlocked pay-per-item.
export function evaluateGate(options: GenOptions, tier: Tier): GateResult {
  const locks: GateLock[] = [];

  if (options.format === 'audiobook') {
    locks.push({ option: 'audiobook', label: 'Audiobooks', requiredTier: 'pro' });
  }

  if (options.format === 'podcast') {
    if (options.depthLevel && !FREE_PODCAST_DEPTHS.includes(options.depthLevel)) {
      locks.push({ option: 'depth', label: 'Longer podcast', requiredTier: 'basic' });
    }
    if ((options.numSpeakers ?? DEFAULT_PODCAST_SPEAKERS) > DEFAULT_PODCAST_SPEAKERS) {
      locks.push({ option: 'speakers', label: 'Extra voices', requiredTier: 'basic' });
    }
  }

  if (options.format === 'tts') {
    if ((options.ttsCharCount ?? 0) > FREE_TTS_CHAR_CAP) {
      locks.push({ option: 'tts-length', label: 'Long read-aloud', requiredTier: 'basic' });
    }
  }

  if (options.tone) locks.push({ option: 'tone', label: 'Custom tone', requiredTier: 'basic' });
  if (options.customVoice) locks.push({ option: 'voice', label: 'Custom voice', requiredTier: 'basic' });

  let requiredTier: Tier | null = null;
  for (const lock of locks) requiredTier = higher(requiredTier, lock.requiredTier);

  const allowed = requiredTier === null || TIER_RANK[tier] >= TIER_RANK[requiredTier];
  return {
    allowed,
    requiredTier: allowed ? null : requiredTier,
    payPerItemEligible: !allowed,
    locks,
  };
}

// Is a single option locked for this tier? Used to render lock badges on controls.
export function isOptionLocked(lock: GateLock, tier: Tier): boolean {
  return TIER_RANK[tier] < TIER_RANK[lock.requiredTier];
}
